import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { accountingAPI, advanceAutoAdjustAPI, formatCurrency } from '../services/api';
import { toast } from 'sonner';
import { Users, ChevronDown, Zap, ToggleLeft, ToggleRight, FileText, FileSpreadsheet, ExternalLink, Download, Edit, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import api from '../services/api';
import { formatDateDisplay } from '../lib/dateUtils';

export default function PartyLedgerPage() {
  const navigate = useNavigate();
  const [parties, setParties] = useState([]);
  const [selectedParty, setSelectedParty] = useState('');
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [autoAdjustEnabled, setAutoAdjustEnabled] = useState(false);
  const [adjusting, setAdjusting] = useState(false);
  const [voucherDetail, setVoucherDetail] = useState(null);

  useEffect(() => {
    accountingAPI.partyList().then(r => setParties(r.data || [])).catch(() => toast.error('Failed to load parties'));
    advanceAutoAdjustAPI.settings().then(r => setAutoAdjustEnabled(r.data?.enabled || false)).catch(() => {});
  }, []);

  const loadLedger = useCallback(async () => {
    if (!selectedParty) return;
    setLoading(true);
    try {
      const params = { page };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await accountingAPI.partyLedger(selectedParty, params);
      setLedger(res.data);
    } catch { toast.error('Failed to load party ledger'); }
    finally { setLoading(false); }
  }, [selectedParty, dateFrom, dateTo, page]);

  useEffect(() => { loadLedger(); }, [loadLedger]);

  const toggleAutoAdjust = async () => {
    const newVal = !autoAdjustEnabled;
    setAutoAdjustEnabled(newVal);
    try {
      await advanceAutoAdjustAPI.updateSettings({ enabled: newVal });
      toast.success(newVal ? 'Auto-adjust enabled' : 'Auto-adjust disabled');
    } catch { toast.error('Failed to save setting'); setAutoAdjustEnabled(!newVal); }
  };

  const handleAutoAdjust = async () => {
    if (!selectedParty) return;
    setAdjusting(true);
    try {
      const res = await advanceAutoAdjustAPI.autoAdjust(selectedParty);
      const d = res.data;
      toast.success(`Adjusted ${d.count} entries — Total Rs.${formatCurrency(d.total_adjusted)}`);
      loadLedger();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Auto-adjust failed');
    } finally { setAdjusting(false); }
  };

  const hasUnadjustedAdvances = ledger?.advance_summary?.total_balance > 0;
  const hasUnpaidInvoices = ledger?.invoices?.some(i => (i.net_payable || i.grand_total || 0) > 0);

  const downloadFile = async (format) => {
    if (!selectedParty) return;
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      const url = `/accounting/party-ledger/${encodeURIComponent(selectedParty)}/${format}?${params.toString()}`;
      const res = await api.get(url, { responseType: 'blob' });
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      const blob = new Blob([res.data]);
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `party_ledger_${selectedParty.replace(/\s+/g, '_')}.${ext}`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success(`Downloaded ${format.toUpperCase()}`);
    } catch { toast.error(`Failed to download ${format.toUpperCase()}`); }
  };

  const openVoucherDetail = async (row) => {
    // For invoice-backed vouchers (sales/purchase), navigate directly to the invoice detail.
    if ((row.voucher_type === 'sales' || row.voucher_type === 'purchase') && row.source_id) {
      navigate(`/invoices/${row.source_id}`);
      return;
    }
    // Otherwise open the journal entry modal using entry_id (voucher_number contains "/"
    // and is NOT a valid path param).
    const targetId = row.entry_id || row.source_id;
    if (!targetId) {
      toast.error('No detail link available for this row');
      return;
    }
    try {
      const res = await accountingAPI.getJournalEntry(targetId);
      setVoucherDetail(res.data);
    } catch {
      toast.error('Could not load voucher details');
    }
  };

  const downloadVoucher = async () => {
    if (!voucherDetail?.voucher_number) return;
    try {
      const acctCode = voucherDetail.lines?.[0]?.account_code;
      if (!acctCode) { toast.error('No account code found'); return; }
      const params = new URLSearchParams();
      params.append('date_from', voucherDetail.date);
      params.append('date_to', voucherDetail.date);
      const url = `/accounting/ledger/${acctCode}/pdf?${params.toString()}`;
      const res = await api.get(url, { responseType: 'blob' });
      const blob = new Blob([res.data]);
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `voucher_${voucherDetail.voucher_number}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success('Voucher downloaded');
    } catch { toast.error('Failed to download voucher'); }
  };

  return (
    <div data-testid="party-ledger-page" className="space-y-5">
      <div className="flex items-center justify-between">
        <div></div>
        <div className="flex items-center gap-3">
          {/* Download buttons */}
          {selectedParty && ledger && (
            <>
              <button onClick={() => downloadFile('pdf')} data-testid="download-party-pdf"
                className="btn-secondary flex items-center gap-2 text-sm">
                <FileText className="w-4 h-4 text-red-500" /> PDF
              </button>
              <button onClick={() => downloadFile('excel')} data-testid="download-party-excel"
                className="btn-secondary flex items-center gap-2 text-sm">
                <FileSpreadsheet className="w-4 h-4 text-green-600" /> Excel
              </button>
            </>
          )}
          {/* Auto-adjust toggle */}
          <button onClick={toggleAutoAdjust} data-testid="auto-adjust-toggle"
            className="flex items-center gap-2 px-3 py-2 rounded-md border border-slate-200 hover:bg-slate-50 transition text-sm">
            {autoAdjustEnabled
              ? <ToggleRight className="w-5 h-5 text-emerald-600" />
              : <ToggleLeft className="w-5 h-5 text-slate-400" />}
            <span className={autoAdjustEnabled ? 'text-emerald-700 font-medium' : 'text-slate-500'}>
              Auto Adjust
            </span>
          </button>
          {/* Manual Adjust button */}
          {selectedParty && hasUnadjustedAdvances && hasUnpaidInvoices && (
            <button onClick={handleAutoAdjust} disabled={adjusting} data-testid="auto-adjust-btn"
              className="btn-primary flex items-center gap-2 text-sm">
              <Zap className="w-4 h-4" />
              {adjusting ? 'Adjusting...' : 'Adjust Advances (FIFO)'}
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Select Party</label>
            <div className="relative">
              <select value={selectedParty}
                onChange={e => { setSelectedParty(e.target.value); setPage(1); }}
                className="w-full appearance-none bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-sm pr-10 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                data-testid="party-ledger-select">
                <option value="">-- Choose Party --</option>
                {parties.map(p => (
                  <option key={p.name} value={p.name}>
                    {p.name} ({p.entry_count} entries{p.has_advances ? `, ${p.advance_count} adv` : ''})
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">From</label>
            <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }}
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
              data-testid="party-ledger-date-from" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">To</label>
            <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }}
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
              data-testid="party-ledger-date-to" />
          </div>
        </div>
      </div>

      {/* Empty State */}
      {!selectedParty ? (
        <div className="bg-white border border-slate-200 rounded-lg flex flex-col items-center justify-center py-24">
          <Users className="w-14 h-14 text-slate-300 mb-4" strokeWidth={1.5} />
          <p className="text-slate-400 text-sm">Select a party to view their ledger</p>
        </div>
      ) : loading ? (
        <div className="bg-white border border-slate-200 rounded-lg flex justify-center py-24">
          <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : ledger ? (
        <div className="space-y-4">
          {/* Party Summary */}
          <div className="bg-white border border-slate-200 rounded-lg p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-heading font-bold text-lg text-slate-900">{ledger.party_name}</h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  {ledger.invoices?.length || 0} invoices | {ledger.advances?.length || 0} advances | {ledger.total_rows} ledger entries
                </p>
              </div>
              <div className="text-right">
                <p className="text-[10px] text-slate-400 uppercase font-semibold tracking-wider">Net Balance</p>
                <p className={`text-2xl font-bold font-mono ${ledger.closing_balance >= 0 ? 'text-blue-700' : 'text-rose-700'}`}>
                  Rs.{formatCurrency(Math.abs(ledger.closing_balance))}
                  <span className="text-xs ml-1.5 font-medium">{ledger.closing_balance >= 0 ? 'Dr' : 'Cr'}</span>
                </p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-red-50 border border-red-100 rounded-lg p-3">
                <p className="text-[10px] text-red-600 font-semibold uppercase tracking-wide">Total Debit</p>
                <p className="text-lg font-bold font-mono text-red-700 mt-0.5">Rs.{formatCurrency(ledger.total_debit)}</p>
              </div>
              <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3">
                <p className="text-[10px] text-emerald-600 font-semibold uppercase tracking-wide">Total Credit</p>
                <p className="text-lg font-bold font-mono text-emerald-700 mt-0.5">Rs.{formatCurrency(ledger.total_credit)}</p>
              </div>
              <div className="bg-slate-50 border border-slate-100 rounded-lg p-3">
                <p className="text-[10px] text-slate-500 font-semibold uppercase tracking-wide">Entries</p>
                <p className="text-lg font-bold font-mono text-slate-800 mt-0.5">{ledger.total_rows}</p>
              </div>
            </div>
          </div>

          {/* Advance Summary */}
          {ledger.advance_summary && (ledger.advance_summary.total_received > 0 || ledger.advance_summary.total_paid > 0) && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-5" data-testid="advance-summary-card">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading font-semibold text-sm text-purple-800 uppercase tracking-wider">Advance Summary</h3>
                {hasUnadjustedAdvances && hasUnpaidInvoices && (
                  <button onClick={handleAutoAdjust} disabled={adjusting} data-testid="advance-summary-adjust-btn"
                    className="text-xs font-medium px-3 py-1.5 rounded-md bg-purple-700 text-white hover:bg-purple-800 transition flex items-center gap-1.5">
                    <Zap className="w-3 h-3" /> {adjusting ? 'Adjusting...' : 'Auto-Adjust FIFO'}
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                <div>
                  <p className="text-[10px] text-purple-600 font-semibold uppercase">Received</p>
                  <p className="text-base font-bold font-mono text-purple-800">Rs.{formatCurrency(ledger.advance_summary.total_received)}</p>
                </div>
                <div>
                  <p className="text-[10px] text-purple-600 font-semibold uppercase">Paid</p>
                  <p className="text-base font-bold font-mono text-purple-800">Rs.{formatCurrency(ledger.advance_summary.total_paid)}</p>
                </div>
                <div>
                  <p className="text-[10px] text-purple-600 font-semibold uppercase">Adjusted</p>
                  <p className="text-base font-bold font-mono text-purple-800">Rs.{formatCurrency(ledger.advance_summary.total_adjusted)}</p>
                </div>
                <div>
                  <p className="text-[10px] text-purple-600 font-semibold uppercase">Unadjusted Balance</p>
                  <p className="text-base font-bold font-mono text-purple-900">Rs.{formatCurrency(ledger.advance_summary.total_balance)}</p>
                </div>
                <div>
                  <p className="text-[10px] text-purple-600 font-semibold uppercase">Active Advances</p>
                  <p className="text-base font-bold font-mono text-purple-900">{ledger.advance_summary.active_count}</p>
                </div>
              </div>
            </div>
          )}

          {/* Invoices Summary — every component (Taxable / GST / TDS / Round Off /
              Advance / Net Payable) is surfaced so the user can trace how the
              Net Balance at the top is derived row-by-row. */}
          {ledger.invoices?.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-lg p-5">
              <h3 className="font-heading font-semibold text-sm text-slate-700 mb-3 uppercase tracking-wider">Invoices</h3>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="party-invoices-table">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-3 py-2 text-left text-[10px] font-semibold text-slate-500 uppercase">S.No.</th>
                      <th className="px-3 py-2 text-left text-[10px] font-semibold text-slate-500 uppercase">Invoice #</th>
                      <th className="px-3 py-2 text-left text-[10px] font-semibold text-slate-500 uppercase">Date</th>
                      <th className="px-3 py-2 text-left text-[10px] font-semibold text-slate-500 uppercase">Type</th>
                      <th className="px-3 py-2 text-right text-[10px] font-semibold text-slate-500 uppercase">Taxable</th>
                      <th className="px-3 py-2 text-right text-[10px] font-semibold text-slate-500 uppercase">GST</th>
                      <th className="px-3 py-2 text-right text-[10px] font-semibold text-slate-500 uppercase">TDS</th>
                      <th className="px-3 py-2 text-right text-[10px] font-semibold text-slate-500 uppercase">Round Off</th>
                      <th className="px-3 py-2 text-right text-[10px] font-semibold text-slate-500 uppercase">Invoice Total</th>
                      <th className="px-3 py-2 text-right text-[10px] font-semibold text-slate-500 uppercase">Advance Adj.</th>
                      <th className="px-3 py-2 text-right text-[10px] font-semibold text-slate-500 uppercase">Net Payable</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {ledger.invoices.map((inv, i) => {
                      const taxable = inv.subtotal !== undefined ? inv.subtotal : 0;
                      const gstVal = (inv.cgst_amount || 0) + (inv.sgst_amount || 0) + (inv.igst_amount || 0) || (inv.gst_total || 0);
                      const tdsVal = inv.tds_enabled && inv.tds_amount > 0 ? inv.tds_amount : 0;
                      const roundVal = inv.round_off || 0;
                      return (
                        <tr key={i} className="hover:bg-slate-50">
                          <td className="px-3 py-2 text-sm text-slate-400">{i + 1}</td>
                          <td className="px-3 py-2 font-mono text-sm">{inv.invoice_number}</td>
                          <td className="px-3 py-2 text-sm text-slate-500">{formatDateDisplay(inv.invoice_date)}</td>
                          <td className="px-3 py-2">
                            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${inv.voucher_type === 'sales' ? 'bg-blue-50 text-blue-700' : 'bg-orange-50 text-orange-700'}`}>{inv.voucher_type}</span>
                          </td>
                          <td className="px-3 py-2 text-right font-mono text-sm text-slate-600">{taxable ? `Rs.${formatCurrency(taxable)}` : '-'}</td>
                          <td className="px-3 py-2 text-right font-mono text-sm text-slate-600">{gstVal ? `Rs.${formatCurrency(gstVal)}` : '-'}</td>
                          <td className="px-3 py-2 text-right font-mono text-sm text-indigo-700">{tdsVal ? `Rs.${formatCurrency(tdsVal)}` : '-'}</td>
                          <td className={`px-3 py-2 text-right font-mono text-sm ${roundVal < 0 ? 'text-rose-600' : roundVal > 0 ? 'text-emerald-700' : 'text-slate-400'}`}>{roundVal ? `${roundVal > 0 ? '+' : ''}Rs.${formatCurrency(roundVal)}` : '-'}</td>
                          <td className="px-3 py-2 text-right font-mono text-sm">Rs.{formatCurrency(inv.grand_total)}</td>
                          <td className="px-3 py-2 text-right font-mono text-sm text-purple-700">{inv.advance_adjusted > 0 ? `Rs.${formatCurrency(inv.advance_adjusted)}` : '-'}</td>
                          <td className="px-3 py-2 text-right font-mono text-sm font-medium">Rs.{formatCurrency(inv.net_payable || inv.grand_total)}</td>
                        </tr>
                      );
                    })}
                    {/* Totals Row — Net Payable sum here MUST reconcile with the
                        header's Net Balance for sales invoices (AR). */}
                    <tr className="bg-slate-50 font-semibold border-t-2 border-slate-300">
                      <td className="px-3 py-2 text-sm" colSpan={4}>Total ({ledger.invoices.length} invoices)</td>
                      <td className="px-3 py-2 text-right font-mono text-sm text-slate-700">Rs.{formatCurrency(ledger.invoices.reduce((s, i) => s + (i.subtotal || 0), 0))}</td>
                      <td className="px-3 py-2 text-right font-mono text-sm text-slate-700">Rs.{formatCurrency(ledger.invoices.reduce((s, i) => s + ((i.cgst_amount || 0) + (i.sgst_amount || 0) + (i.igst_amount || 0) || (i.gst_total || 0)), 0))}</td>
                      <td className="px-3 py-2 text-right font-mono text-sm text-indigo-700">Rs.{formatCurrency(ledger.invoices.reduce((s, i) => s + (i.tds_enabled && i.tds_amount > 0 ? i.tds_amount : 0), 0))}</td>
                      <td className="px-3 py-2 text-right font-mono text-sm text-slate-600">Rs.{formatCurrency(ledger.invoices.reduce((s, i) => s + (i.round_off || 0), 0))}</td>
                      <td className="px-3 py-2 text-right font-mono text-sm">Rs.{formatCurrency(ledger.invoices.reduce((s, i) => s + (i.grand_total || 0), 0))}</td>
                      <td className="px-3 py-2 text-right font-mono text-sm text-purple-700">Rs.{formatCurrency(ledger.invoices.reduce((s, i) => s + (i.advance_adjusted || 0), 0))}</td>
                      <td className="px-3 py-2 text-right font-mono text-sm">Rs.{formatCurrency(ledger.invoices.reduce((s, i) => s + (i.net_payable || i.grand_total || 0), 0))}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-slate-400 mt-3">
                Invoice Total = Taxable + GST − TDS + Round Off. Net Payable = Invoice Total − Advance Adjusted.
              </p>
            </div>
          )}

          {/* Advances Detail */}
          {ledger.advances?.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-lg p-5">
              <h3 className="font-heading font-semibold text-sm text-slate-700 mb-3 uppercase tracking-wider">Advances</h3>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="party-advances-table">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-3 py-2 text-left text-[10px] font-semibold text-slate-500 uppercase">S.No.</th>
                      <th className="px-3 py-2 text-left text-[10px] font-semibold text-slate-500 uppercase">Voucher</th>
                      <th className="px-3 py-2 text-left text-[10px] font-semibold text-slate-500 uppercase">Date</th>
                      <th className="px-3 py-2 text-left text-[10px] font-semibold text-slate-500 uppercase">Type</th>
                      <th className="px-3 py-2 text-right text-[10px] font-semibold text-slate-500 uppercase">Amount</th>
                      <th className="px-3 py-2 text-right text-[10px] font-semibold text-slate-500 uppercase">Adjusted</th>
                      <th className="px-3 py-2 text-right text-[10px] font-semibold text-slate-500 uppercase">Balance</th>
                      <th className="px-3 py-2 text-right text-[10px] font-semibold text-slate-500 uppercase">Status</th>
                      <th className="px-3 py-2 text-center text-[10px] font-semibold text-slate-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {ledger.advances.map((adv, i) => (
                      <tr key={i} className="hover:bg-slate-50">
                        <td className="px-3 py-2 text-sm text-slate-400">{i + 1}</td>
                        <td className="px-3 py-2 font-mono text-sm">{adv.voucher_number}</td>
                        <td className="px-3 py-2 text-sm text-slate-500">{formatDateDisplay(adv.date)}</td>
                        <td className="px-3 py-2">
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${adv.type === 'received' ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>{adv.type}</span>
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-sm">Rs.{formatCurrency(adv.amount)}</td>
                        <td className="px-3 py-2 text-right font-mono text-sm text-purple-700">Rs.{formatCurrency(adv.adjusted_amount || 0)}</td>
                        <td className="px-3 py-2 text-right font-mono text-sm font-medium">Rs.{formatCurrency(adv.balance_amount)}</td>
                        <td className="px-3 py-2 text-right">
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${adv.balance_amount <= 0 ? 'bg-slate-100 text-slate-500' : 'bg-emerald-50 text-emerald-700'}`}>
                            {adv.balance_amount > 0 ? 'active' : 'fully adjusted'}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <button onClick={() => navigate('/advances')} className="p-1 hover:bg-slate-100 rounded" title="Edit advance" data-testid={`edit-advance-${i}`}>
                              <Edit className="w-3.5 h-3.5 text-slate-500" />
                            </button>
                            <button onClick={async (e) => {
                              e.stopPropagation();
                              if (!window.confirm(`Delete advance ${adv.voucher_number}?`)) return;
                              try {
                                await api.delete(`/advances/${adv.advance_id}`);
                                toast.success('Advance deleted');
                                loadLedger();
                              } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
                            }} className="p-1 hover:bg-rose-50 rounded" title="Delete advance" data-testid={`delete-advance-${i}`}>
                              <Trash2 className="w-3.5 h-3.5 text-rose-500" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Unified Ledger Table */}
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-200 bg-slate-50">
              <h3 className="font-heading font-semibold text-sm text-slate-700 uppercase tracking-wider">Unified Ledger</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="party-ledger-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">S.No.</th>
                    <th className="px-4 py-3 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Date</th>
                    <th className="px-4 py-3 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Voucher</th>
                    <th className="px-4 py-3 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Type</th>
                    <th className="px-4 py-3 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Narration</th>
                    <th className="px-4 py-3 text-right text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Debit</th>
                    <th className="px-4 py-3 text-right text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Credit</th>
                    <th className="px-4 py-3 text-right text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Balance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(ledger.ledger || []).map((row, idx) => {
                    const isAdvance = (row.source_type || '').startsWith('advance');
                    const typeColors = {
                      advance_receipt: 'bg-emerald-50 text-emerald-700',
                      advance_payment: 'bg-rose-50 text-rose-700',
                      advance_adjustment: 'bg-purple-50 text-purple-700',
                      sales: 'bg-blue-50 text-blue-700',
                      purchase: 'bg-orange-50 text-orange-700',
                    };
                    return (
                      <tr key={idx} className={`hover:bg-slate-50 transition-colors cursor-pointer ${isAdvance ? 'bg-purple-50/30' : ''}`} data-testid={`party-ledger-row-${idx}`}
                        onClick={() => openVoucherDetail(row)}>
                        <td className="px-4 py-3 text-sm text-slate-400">{idx + 1}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{formatDateDisplay(row.date)}</td>
                        <td className="px-4 py-3 font-mono text-xs text-[hsl(221,100%,50%)] hover:underline flex items-center gap-1">{row.voucher_number} <ExternalLink className="w-3 h-3 opacity-40" /></td>
                        <td className="px-4 py-3">
                          <span className={`text-[10px] font-semibold capitalize px-2 py-0.5 rounded-full ${typeColors[row.voucher_type] || 'bg-slate-100 text-slate-600'}`}>
                            {row.voucher_type?.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 max-w-[260px] truncate">{row.narration || '-'}</td>
                        <td className="px-4 py-3 text-right font-mono text-sm">
                          {row.debit > 0 ? <span className="text-red-600 font-medium">Rs.{formatCurrency(row.debit)}</span> : <span className="text-slate-300">-</span>}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-sm">
                          {row.credit > 0 ? <span className="text-emerald-600 font-medium">Rs.{formatCurrency(row.credit)}</span> : <span className="text-slate-300">-</span>}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-sm font-bold">
                          <span className={row.balance >= 0 ? 'text-red-600' : 'text-emerald-600'}>
                            Rs.{formatCurrency(Math.abs(row.balance))} {row.balance >= 0 ? 'Dr' : 'Cr'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                  {(!ledger.ledger || ledger.ledger.length === 0) && (
                    <tr><td colSpan={8} className="text-center text-slate-400 py-16 text-sm">No entries for this party</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {ledger.pages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50">
                <p className="text-sm text-slate-500">Page {page} of {ledger.pages} ({ledger.total_rows} entries)</p>
                <div className="flex gap-2">
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                    className="px-3 py-1.5 text-xs font-medium border border-slate-200 rounded-md hover:bg-white transition disabled:opacity-40">Previous</button>
                  <button onClick={() => setPage(p => Math.min(ledger.pages, p + 1))} disabled={page >= ledger.pages}
                    className="px-3 py-1.5 text-xs font-medium border border-slate-200 rounded-md hover:bg-white transition disabled:opacity-40">Next</button>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : null}

      {/* Voucher Detail Dialog */}
      <Dialog open={!!voucherDetail} onOpenChange={(o) => !o && setVoucherDetail(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading font-semibold tracking-tight flex items-center gap-2">
              Voucher: {voucherDetail?.voucher_number}
            </DialogTitle>
          </DialogHeader>
          {voucherDetail && (
            <div className="space-y-3 pt-2">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="form-label">Date</span><p className="font-medium">{voucherDetail.date}</p></div>
                <div><span className="form-label">Type</span><p className="font-medium capitalize">{voucherDetail.voucher_type}</p></div>
                {voucherDetail.party_name && <div><span className="form-label">Party</span><p className="font-medium">{voucherDetail.party_name}</p></div>}
                {voucherDetail.narration && <div className="col-span-2"><span className="form-label">Narration</span><p className="text-sm">{voucherDetail.narration}</p></div>}
              </div>
              {voucherDetail.lines?.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full data-table" data-testid="voucher-lines-table">
                    <thead><tr><th>Account</th><th className="text-right">Debit</th><th className="text-right">Credit</th></tr></thead>
                    <tbody>
                      {voucherDetail.lines.map((line, i) => (
                        <tr key={i}>
                          <td className="text-sm">{line.account_name || line.account_code}</td>
                          <td className="text-right font-mono text-sm">{line.debit > 0 ? `Rs.${formatCurrency(line.debit)}` : '-'}</td>
                          <td className="text-right font-mono text-sm">{line.credit > 0 ? `Rs.${formatCurrency(line.credit)}` : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="flex gap-2 pt-2">
                <button onClick={downloadVoucher} className="btn-secondary flex items-center gap-2 text-[13px]" data-testid="download-voucher-pdf">
                  <Download className="w-3.5 h-3.5" /> Download PDF
                </button>
                <button onClick={() => { setVoucherDetail(null); navigate('/voucher-entries'); }} className="btn-secondary flex items-center gap-2 text-[13px]" data-testid="go-to-voucher">
                  <ExternalLink className="w-3.5 h-3.5" /> Open in Journal
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
